// src/components/DeadlineCard.js
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  CircularProgress, 
  Divider, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ja from 'date-fns/locale/ja';
import { motion } from 'framer-motion';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { differenceInCalendarDays, format } from 'date-fns';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import { designTokens, commonCardStylesDark } from '../../../theme/designTokens';
import { overlay, text as textColors } from '../../../utils/darkModeAdapter';

// DeadlineService API統合
const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = `${BACKEND_URL}/deadlines/`;

/**
 * DeadlineCard:
 * カスタム締め切りを追加・削除できるように機能拡張
 * DeadlineServiceの機能を統合済み
 */
function DeadlineCard({ todayStr }) {
  const theme = useTheme();
  const [deadlines, setDeadlines] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editingDeadlines, setEditingDeadlines] = useState({
    es: null,
    naitei: null,
    additionalDeadlines: []
  });
  
  // 新規追加用の一時データ
  const [newDeadline, setNewDeadline] = useState({
    name: '',
    date: null,
    description: ''
  });

  // 編集中の締め切りのインデックス（-1は編集中でないことを示す）
  const [editingIndex, setEditingIndex] = useState(-1);
  
  // APIから締め切り情報を取得
  useEffect(() => {
    fetchDeadlines();
  }, []);
  
  // 締め切り情報取得API
  const fetchDeadlines = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(API_URL, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        withCredentials: true
      });
      
      // データ整形: スネークケース→キャメルケース
      if (Array.isArray(response.data)) {
        // リストなら最初の要素を使う
        const d = response.data[0] || {};
        setDeadlines({
          ...d,
          additionalDeadlines: d.additional_deadlines ?? [],
          es: d.es_date ?? null,
          naitei: d.naitei_date ?? null,
        });
      } else {
        setDeadlines({
          ...response.data,
          additionalDeadlines: response.data.additional_deadlines ?? [],
          es: response.data.es_date ?? null,
          naitei: response.data.naitei_date ?? null,
        });
      }
      
      setError(null);
      setLoading(false);
    } catch (error) {
      console.error('締め切り情報の取得に失敗しました', error);
      
      // エラー状態でもデフォルト値を使用してUIを表示する
      setDeadlines({
        es: null,
        naitei: null,
        additionalDeadlines: []
      });
      
      // APIエラーを収集するが、ユーザーにはエラー表示しない
      setError(null);
      setLoading(false);
    }
  };
  
  // 締め切り情報をIDで取得
  const getDeadlineById = async (id) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        withCredentials: true  // CORS対応のためのクレデンシャル設定を追加
      });
      
      return response.data;
    } catch (error) {
      console.error('締め切り情報の取得に失敗しました', error);
      return null; // エラー時にnullを返す
    }
  };
  
  // 締め切り情報を保存
  const saveDeadline = async (deadlineData) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // 日付データをISOString形式に変換し、未入力の追加締め切りは除去
      const formattedData = {
        es_date: deadlineData.es ? deadlineData.es.toISOString() : null,
        naitei_date: deadlineData.naitei ? deadlineData.naitei.toISOString() : null,
        additional_deadlines: (deadlineData.additionalDeadlines || [])
          .filter(d => d.name && d.date) // nameとdate両方が存在する項目のみ送信
          .map(d => ({
            ...d,
            date: d.date ? new Date(d.date).toISOString() : null
          }))
      };

      console.log('送信データ:', formattedData); // デバッグ用

      const response = await axios.post(API_URL, formattedData, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        withCredentials: true
      });
      
      // レスポンスデータを整形してステートを更新
      const responseData = response.data;
      setDeadlines({
        ...responseData,
        additionalDeadlines: responseData.additional_deadlines ?? [],
        es: responseData.es_date ?? null,
        naitei: responseData.naitei_date ?? null,
      });
      
      setError(null);
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error('締め切り情報の保存に失敗しました', error);
      console.error('エラー詳細:', error.response?.data); // バックエンドからのエラー詳細を表示
      setLoading(false);
      return null;
    }
  };
  
  // 締め切り情報を削除
  const deleteDeadline = async (id) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.delete(`${API_URL}/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        withCredentials: true  // CORS対応のためのクレデンシャル設定を追加
      });
      
      // 削除成功後に最新データを取得
      fetchDeadlines();
      return response.data;
    } catch (error) {
      console.error('締め切り情報の削除に失敗しました', error);
      return null; // エラー時にnullを返す
    }
  };
  
  // プロジェクトに紐づく締め切り情報を取得
  const getDeadlineByProject = async (projectId) => {
    try {
      const allDeadlines = await fetchDeadlines();
      return allDeadlines?.find(d => d.projectId === projectId) || null;
    } catch (error) {
      console.error('プロジェクト締め切り情報の取得に失敗しました', error);
      return null;
    }
  };
  
  // 汎用的な締め切り情報を取得
  const getGeneralDeadline = async () => {
    try {
      const allDeadlines = await fetchDeadlines();
      return allDeadlines?.find(d => !d.projectId) || null;
    } catch (error) {
      console.error('一般的な締め切り情報の取得に失敗しました', error);
      return null;
    }
  };
  
  // 現在日を Date オブジェクトとして取得
  const today = new Date();
  
  // 締め切り日からの日数を計算（既存データがなければデフォルト値を使用）
  const esDeadline = deadlines?.es || deadlines?.es_date;
  const naiteiDeadline = deadlines?.naitei || deadlines?.naitei_date;
  
  // 日付オブジェクトに変換して検証
  const esDateObj = esDeadline ? new Date(esDeadline) : null;
  const naiteiDateObj = naiteiDeadline ? new Date(naiteiDeadline) : null;
  const validEsDate = esDateObj && !isNaN(esDateObj);
  const validNaiteiDate = naiteiDateObj && !isNaN(naiteiDateObj);
  
  // 追加の締め切り
  const additionalDeadlinesRaw = deadlines?.additionalDeadlines || deadlines?.additional_deadlines || [];
  const additionalDeadlines = additionalDeadlinesRaw.map(d => ({
    ...d,
    date: d.date ? new Date(d.date) : null
  }));
  
  // ES締切日までの残り日数
  const daysToEs = validEsDate ? 
    Math.max(0, differenceInCalendarDays(esDateObj, today)) : 14;
  
  // 内定締切日までの残り日数
  const daysToNaitei = validNaiteiDate ? 
    Math.max(0, differenceInCalendarDays(naiteiDateObj, today)) : 30;
  
  // 仮の計算: "すでに○日経過" は適宜ご自身のロジックに合わせて修正してください
  const totalDaysES = Math.max(1, daysToEs + 10);
  const totalDaysNaitei = Math.max(1, daysToNaitei + 20);

  // 経過日数
  const elapsedEs = totalDaysES - daysToEs;
  const elapsedNaitei = totalDaysNaitei - daysToNaitei;
  
  // 追加の締め切りごとの残り日数と経過日数を計算
  const calculateDaysForDeadline = (deadlineDate) => {
    if (!deadlineDate || isNaN(new Date(deadlineDate))) return { daysLeft: 0, elapsedDays: 0 };
    
    const date = new Date(deadlineDate);
    const daysLeft = Math.max(0, differenceInCalendarDays(date, today));
    // 仮の経過日数計算（実際のロジックに合わせて変更してください）
    const totalDays = daysLeft + 14; // 任意の既定値
    const elapsedDays = totalDays - daysLeft;
    
    return { daysLeft, elapsedDays };
  };
  
  // 編集ダイアログを開く
  const handleOpenDialog = () => {
    setEditingDeadlines({
      es: validEsDate ? esDateObj : null,
      naitei: validNaiteiDate ? naiteiDateObj : null,
      additionalDeadlines: additionalDeadlines.map(deadline => ({
        ...deadline,
        date: deadline.date ? new Date(deadline.date) : null
      }))
    });

    // 新規追加用のデータをリセット
    setNewDeadline({
      name: '',
      date: null,
      description: ''
    });

    // 編集モードをリセット
    setEditingIndex(-1);

    setOpen(true);
  };
  
  // 編集ダイアログを閉じる
  const handleCloseDialog = () => {
    setOpen(false);
  };
  
  // 期限の更新を保存
  const handleSaveDeadlines = async () => {
    try {
      // 追加の締め切りの日付を検証
      const validatedAdditionalDeadlines = editingDeadlines.additionalDeadlines
        .filter(d => d.name && d.date) // 名前と日付が両方ある項目のみ
        .map(d => ({
          ...d,
          date: d.date instanceof Date ? d.date : new Date(d.date)
        }))
        .filter(d => !isNaN(d.date)); // 有効な日付のみ

      // APIリクエストを行い、期限を更新
      const result = await saveDeadline({
        es: editingDeadlines.es,
        naitei: editingDeadlines.naitei,
        additionalDeadlines: validatedAdditionalDeadlines
      });
      
      if (result) {
        // 保存成功
        setOpen(false);
      } else {
        // API呼び出しは失敗したが、UIには何も表示せず閉じる
        setOpen(false);
      }
    } catch (error) {
      console.error('締め切り情報の保存に失敗しました', error);
      console.error('エラー詳細:', error.response?.data);
      setOpen(false); // エラー時も閉じる
    }
  };
  
  // 展開パネルの切り替え
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };
  
  // 追加の締め切りを追加
  const handleAddDeadline = () => {
    // 名前と日付の両方が入力されている場合のみ追加
    if (!newDeadline.name || !newDeadline.date) {
      console.warn('締め切り名と日付は必須です');
      return;
    }
    
    // 日付が有効なDate型であることを確認
    const dateObj = new Date(newDeadline.date);
    if (isNaN(dateObj)) {
      console.warn('無効な日付形式です');
      return;
    }
    
    setEditingDeadlines(prev => ({
      ...prev,
      additionalDeadlines: [
        ...prev.additionalDeadlines,
        {
          name: newDeadline.name,
          date: dateObj, // 必ずDate型で保存
          description: newDeadline.description || ''
        }
      ]
    }));
    
    // 入力フォームをクリア
    setNewDeadline({
      name: '',
      date: null,
      description: ''
    });
  };
  
  // 特定の締め切りを削除
  const handleDeleteDeadline = (index) => {
    setEditingDeadlines(prev => ({
      ...prev,
      additionalDeadlines: prev.additionalDeadlines.filter((_, i) => i !== index)
    }));
  };

  // 締め切りを編集
  const handleEditDeadline = (index) => {
    const deadline = editingDeadlines.additionalDeadlines[index];
    setNewDeadline({
      name: deadline.name,
      date: deadline.date,
      description: deadline.description || ''
    });
    setEditingIndex(index);
  };

  // 編集中の締め切りを保存
  const handleSaveEditedDeadline = () => {
    if (!newDeadline.name || !newDeadline.date) {
      console.warn('締め切り名と日付は必須です');
      return;
    }

    const dateObj = new Date(newDeadline.date);
    if (isNaN(dateObj)) {
      console.warn('無効な日付形式です');
      return;
    }

    setEditingDeadlines(prev => ({
      ...prev,
      additionalDeadlines: prev.additionalDeadlines.map((deadline, i) =>
        i === editingIndex
          ? {
              name: newDeadline.name,
              date: dateObj,
              description: newDeadline.description || ''
            }
          : deadline
      )
    }));

    // 編集モードをリセット
    setNewDeadline({
      name: '',
      date: null,
      description: ''
    });
    setEditingIndex(-1);
  };

  // 編集をキャンセル
  const handleCancelEdit = () => {
    setNewDeadline({
      name: '',
      date: null,
      description: ''
    });
    setEditingIndex(-1);
  };
  
  // 標準の締め切りをリセット
  const handleResetStandardDeadline = (type) => {
    if (type === 'es') {
      setEditingDeadlines(prev => ({
        ...prev,
        es: null
      }));
    } else if (type === 'naitei') {
      setEditingDeadlines(prev => ({
        ...prev,
        naitei: null
      }));
    }
  };

  // ローディング表示
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // エラー状態の場合は明示的なUIを表示せず、デフォルト状態でレンダリング

  return (
    <Card
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      sx={{
        ...commonCardStylesDark(theme),
        mb: 3,
        cursor: 'pointer',
        backgroundColor: '#FFEB3B0D',
      }}
    >
      <CardContent
        component={motion.div}
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        sx={{ p: 3 }}
      >
        {/* タイトル & 日付 & 編集ボタン */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              letterSpacing: '0.5px',
              color: designTokens.colors.warning,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <AccessTimeIcon
              sx={{
                fontSize: '1.2rem',
                mr: 1,
                color: designTokens.colors.warning,
              }}
            />
            締め切り状況
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'nowrap',
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                px: 1.5,
                py: 0.5,
                borderRadius: 10,
                fontWeight: 500,
                color: designTokens.colors.warning,
                mr: 1
              }}
            >
              {todayStr} (本日)
            </Typography>

            <Tooltip title="締め切り日を編集">
              <IconButton
                size="small"
                onClick={handleOpenDialog}
                sx={{
                  color: designTokens.colors.warning,
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 152, 0, 0.2)'
                  },
                  width: 32,
                  height: 32
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {/* デフォルトの円グラフ (ES / 内定) */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 2, sm: 6 },
              flexWrap: 'wrap',
              justifyContent: 'space-evenly',
              alignItems: 'center',
              mb: 2,
            }}
          >
            {/* ES: 経過→グレー, 残り→青 */}
            {validEsDate && (
              <DeadlineDualCircle
                label="ES"
                daysLeft={daysToEs}
                elapsedDays={elapsedEs}
                size={150}
                colorLeft="primary"   // 残り: 青
                variant="outlined"
                deadline={format(esDateObj, 'yyyy/MM/dd')}
              />
            )}

            {/* 内定: 経過→グレー, 残り→黄 */}
            {validNaiteiDate && (
              <DeadlineDualCircle
                label="内定"
                daysLeft={daysToNaitei}
                elapsedDays={elapsedNaitei}
                size={150}
                colorLeft="warning"   // 残り: 黄
                variant="outlined"
                deadline={format(naiteiDateObj, 'yyyy/MM/dd')}
              />
            )}
            
            {/* 追加の締め切り項目を表示 */}
            {additionalDeadlines.map((deadline, index) => {
              const dateObj = deadline.date ? new Date(deadline.date) : null;
              const validDate = dateObj && !isNaN(dateObj);
              const { daysLeft, elapsedDays } = calculateDaysForDeadline(dateObj);
              return (
                <DeadlineDualCircle
                  key={`deadline-${index}`}
                  label={deadline.name}
                  daysLeft={daysLeft}
                  elapsedDays={elapsedDays}
                  size={150}
                  colorLeft="success"  // デフォルトは緑
                  variant="outlined"
                  deadline={validDate ? format(dateObj, 'yyyy/MM/dd') : '未設定'}
                  description={deadline.description}
                />
              );
            })}
            
            {/* 締め切りがない場合のメッセージ */}
            {!validEsDate && !validNaiteiDate && additionalDeadlines.length === 0 && (
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  py: 4, 
                  width: '100%', 
                  color: 'text.secondary' 
                }}
              >
                <Typography variant="body1" sx={{ mb: 2 }}>
                  締め切りがまだ設定されていません
                </Typography>
                <Button 
                  variant="outlined" 
                  color="warning" 
                  startIcon={<AddIcon />}
                  onClick={handleOpenDialog}
                >
                  締め切りを追加
                </Button>
              </Box>
            )}
          </Box>
        </Box>
        
        {/* 詳細情報表示用の拡張パネル */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button
            size="small"
            onClick={toggleExpanded}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }}
              />
            }
            sx={{
              color: designTokens.colors.warning,
              textTransform: 'none',
              fontSize: '0.85rem',
              '&:hover': {
                backgroundColor: 'rgba(255, 152, 0, 0.1)'
              }
            }}
          >
            {expanded ? '詳細を隠す' : '詳細を表示'}
          </Button>
        </Box>
        
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, p: 2, bgcolor: overlay(theme).whiteMedium, borderRadius: 2 }}>
            {/* デフォルトの締め切り */}
            {validEsDate && (
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: textColors(theme).secondary }}>
                ES提出締切: {format(esDateObj, 'yyyy年MM月dd日')}
              </Typography>
            )}
            
            {validNaiteiDate && (
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: textColors(theme).secondary }}>
                内定締切: {format(naiteiDateObj, 'yyyy年MM月dd日')}
              </Typography>
            )}
            
            {/* 追加の締め切り */}
            {additionalDeadlines.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: designTokens.colors.warning }}>
                  追加の締め切り
                </Typography>
                
                {additionalDeadlines.map((deadline, index) => {
                  const dateObj = deadline.date ? new Date(deadline.date) : null;
                  const validDate = dateObj && !isNaN(dateObj);
                  const { daysLeft, elapsedDays } = calculateDaysForDeadline(dateObj);

                  return (
                    <Box key={index} sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: textColors(theme).secondary }}>
                        {deadline.name}: {validDate ? format(dateObj, 'yyyy年MM月dd日') : '未設定'}
                      </Typography>
                      {deadline.description && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 1 }}>
                          {deadline.description}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </>
            )}
            
            <Box sx={{ mt: 2 }}>
              <Button 
                size="small" 
                variant="outlined" 
                color="warning" 
                startIcon={<EditIcon />}
                onClick={handleOpenDialog}
              >
                締め切り日を編集
              </Button>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
      
      {/* 締め切り設定用ダイアログ */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: designTokens.colors.warning }}>
          締め切り日の設定
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
            {/* デフォルト締め切り */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: designTokens.colors.warning }}>
                標準の締め切り
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        ES提出締め切り
                      </Typography>
                      <DatePicker
                        value={editingDeadlines.es}
                        onChange={(newDate) => setEditingDeadlines({...editingDeadlines, es: newDate})}
                        slotProps={{ 
                          textField: { 
                            fullWidth: true,
                            size: "small",
                            helperText: "ES提出の締め切り日を選択してください" 
                          } 
                        }}
                      />
                    </Box>
                    <IconButton 
                      color="error" 
                      sx={{ ml: 1 }}
                      onClick={() => handleResetStandardDeadline('es')}
                      disabled={!editingDeadlines.es}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        内定締め切り
                      </Typography>
                      <DatePicker
                        value={editingDeadlines.naitei}
                        onChange={(newDate) => setEditingDeadlines({...editingDeadlines, naitei: newDate})}
                        slotProps={{ 
                          textField: { 
                            fullWidth: true,
                            size: "small",
                            helperText: "内定の締め切り日を選択してください" 
                          } 
                        }}
                      />
                    </Box>
                    <IconButton 
                      color="error" 
                      sx={{ ml: 1 }}
                      onClick={() => handleResetStandardDeadline('naitei')}
                      disabled={!editingDeadlines.naitei}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            {/* カスタム締め切り一覧 */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: designTokens.colors.warning }}>
                追加の締め切り
              </Typography>
              
              {editingDeadlines.additionalDeadlines.length > 0 ? (
                <List>
                  {editingDeadlines.additionalDeadlines.map((deadline, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        bgcolor: overlay(theme).whiteMedium,
                        mb: 1,
                        borderRadius: 1,
                        border: '1px solid rgba(0, 0, 0, 0.08)'
                      }}
                    >
                      <ListItemText
                        primary={deadline.name}
                        secondary={
                          <>
                            <Typography variant="body2" component="span">
                              {deadline.date ? format(deadline.date, 'yyyy年MM月dd日') : '日付未設定'}
                            </Typography>
                            {deadline.description && (
                              <Typography variant="caption" component="p" sx={{ mt: 0.5 }}>
                                {deadline.description}
                              </Typography>
                            )}
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="edit"
                          onClick={() => handleEditDeadline(index)}
                          color="primary"
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDeleteDeadline(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  追加の締め切りはまだありません。
                </Typography>
              )}
            </Box>
            
            {/* 新規締め切り追加フォーム */}
            <Box sx={{ bgcolor: overlay(theme).whiteMedium, p: 2, borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <Typography variant="subtitle2" sx={{ color: designTokens.colors.warning, mb: 2 }}>
                {editingIndex >= 0 ? '締め切りを編集' : '新しい締め切りを追加'}
              </Typography>

              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} md={4}>
                  <TextField
                    label="締め切り名"
                    value={newDeadline.name}
                    onChange={(e) => setNewDeadline({...newDeadline, name: e.target.value})}
                    fullWidth
                    size="small"
                    required
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="締め切り日"
                    value={newDeadline.date}
                    onChange={(newDate) => setNewDeadline({...newDeadline, date: newDate})}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex' }}>
                    <TextField
                      label="メモ（任意）"
                      value={newDeadline.description}
                      onChange={(e) => setNewDeadline({...newDeadline, description: e.target.value})}
                      fullWidth
                      size="small"
                    />
                    {editingIndex >= 0 ? (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          sx={{ ml: 1, minWidth: '100px' }}
                          onClick={handleSaveEditedDeadline}
                          disabled={!newDeadline.name || !newDeadline.date}
                        >
                          更新
                        </Button>
                        <Button
                          variant="outlined"
                          color="inherit"
                          sx={{ ml: 1, minWidth: '100px' }}
                          onClick={handleCancelEdit}
                        >
                          キャンセル
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ ml: 1, minWidth: '100px' }}
                        onClick={handleAddDeadline}
                        disabled={!newDeadline.name || !newDeadline.date}
                      >
                        追加
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
            
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            キャンセル
          </Button>
          <Button 
            onClick={handleSaveDeadlines} 
            variant="contained" 
            color="warning"
            startIcon={<SaveIcon />}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default DeadlineCard;

/**
 * DeadlineDualCircle:
 * - 下のリング(グレー)で常に 100% を描画 (経過部分の土台)
 * - 上のリング(指定色)で daysLeft を実際の率に変換して部分的に上書き
 * → 残り部分だけ色付き、経過分はグレーが露出する形
 */
function DeadlineDualCircle({
  label,
  daysLeft,
  elapsedDays,
  size = 150,
  colorLeft, // 残り日数の色
  variant = 'outlined',
  deadline,
  description
}) {
  const theme = useTheme();
  // 合計日数 = (残り + 経過)
  const totalDays = daysLeft + elapsedDays;
  // 0~100%にクランプ
  const progressLeft = totalDays > 0
    ? Math.min((daysLeft / totalDays) * 100, 100)
    : 0;

  // 色の設定
  const getColor = () => {
    if (colorLeft === 'primary') return { main: designTokens.colors.primary, light: '#bbdefb' };
    if (colorLeft === 'warning') return { main: designTokens.colors.warning, light: '#ffe0b2' };
    if (colorLeft === 'success') return { main: designTokens.colors.success, light: '#c8e6c9' };
    if (colorLeft === 'error') return { main: designTokens.colors.error, light: '#ffcdd2' };
    if (colorLeft === 'info') return { main: designTokens.colors.info, light: '#b3e5fc' };
    return { main: designTokens.colors.neutral[600], light: designTokens.colors.neutral[200] };
  };

  const colors = getColor();
  const isAlmostDue = daysLeft <= 3;

  return (
    <Box 
      sx={{ 
        textAlign: 'center', 
        minWidth: size,
        backgroundColor: variant === 'outlined' ? overlay(theme).whiteLight : 'transparent',
        p: variant === 'outlined' ? 3 : 0,
        borderRadius: 4,
        border: variant === 'outlined' ? '1px solid rgba(255,255,255,0.8)' : 'none',
        boxShadow: variant === 'outlined' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none',
      }}
      component={motion.div}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        {/* 経過: グレー (下層) = 100%表示 */}
        <CircularProgress
          variant="determinate"
          value={100}
          color="inherit"
          size={size}
          thickness={6}
          sx={{
            position: 'absolute',
            color: overlay(theme).medium,
            transform: 'rotate(-90deg)', // 時計回りに見せる
          }}
        />
        {/* 残り日数: 指定色 (上層) */}
        <CircularProgress
          variant="determinate"
          value={progressLeft}
          size={size}
          thickness={6}
          sx={{
            transform: 'rotate(-90deg)',
            color: colors.main,
            transition: 'all 0.3s ease',
            ...(isAlmostDue && {
              color: designTokens.colors.error,
              '& .MuiCircularProgress-circle': {
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%': { stroke: designTokens.colors.error },
                  '50%': { stroke: '#ff7961' },
                  '100%': { stroke: designTokens.colors.error },
                }
              }
            })
          }}
        />

        {/* 真ん中のラベル */}
        <Box
          sx={{
            position: 'absolute',
            top: 0, left: 0, bottom: 0, right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            p: 1,
          }}
        >
          <Typography
            variant="body1"
            sx={{ 
              fontWeight: 'bold', 
              fontSize: '1.1rem', 
              mb: 0.5,
              color: colors.main,
              textAlign: 'center',
              lineHeight: 1.2,
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>

      {/* 下部に「あと◯日 / すでに◯日」表示 */}
      <Box 
        sx={{ 
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: 'bold',
            color: isAlmostDue ? designTokens.colors.error : colors.main,
            fontSize: '1.1rem',
          }}
          component={motion.div}
          animate={isAlmostDue ? { scale: [1, 1.05, 1] } : {}}
          transition={isAlmostDue ? {
            repeat: Infinity,
            duration: 2
          } : {}}
        >
          あと {daysLeft} 日
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'medium',
            color: 'text.secondary',
            fontSize: '0.9rem',
          }}
        >
          すでに {elapsedDays} 日経過
        </Typography>
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            color: 'text.secondary',
            fontSize: '0.8rem',
            padding: '2px 8px',
            backgroundColor: overlay(theme).light,
            borderRadius: 5,
            display: 'inline-block',
            margin: '0 auto',
          }}
        >
          期限: {deadline}
        </Typography>
        
        {description && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.75rem',
              maxWidth: size,
              mt: 0.5,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
}